import { ComplexValue, Value } from 'app/types-shared';
import { WithMetroLineWrappedComponentProps } from 'app/components/metro-line/types';
import { SubscriberGroupsTranslation } from '../constants';
import { SubscriberGroupSectionType } from './metroline/types';
import {
  SubscriberGroupsFormState, SubscriberGroupItem, SubscriberGroupItemValidation,
  ExternalMemberValue
} from './types-store';

export interface SubscriberGroupsItemWire {
  code: Value;
  language: Value[];
  externalMember: ExternalMemberWire[];
  selfService?: boolean;
  disabled?: string;
}

export interface SubscriberGroupsSectionProps {
  actionType: SubscriberGroupActionType;
  subscriberGroupItem: SubscriberGroupItem;
  locale: string;
  translation: SubscriberGroupsTranslation;
  section: SubscriberGroupSectionType;
  fetching?: boolean;
  infoText?: string;
  validation: SubscriberGroupItemValidation;
  updateItem: (item: Partial<SubscriberGroupItem>) => void;
  displayError: (errorMessage: string) => void;
  updateExternalMemberItem: (value: ExternalMemberValue) => void;
  removeExternalMember: (id: number) => void;
  addExternalMember: () => void;
  resetValidation: (id: number, key?: string) => void;
  removeAllExternalMembers: () => void;
}

export type SubscriberGroupsSection = SubscriberGroupsSectionProps & WithMetroLineWrappedComponentProps<SubscriberGroupSectionType>;

export interface SubscriberGroupContainerProps {
  actionType: SubscriberGroupActionType;
  formState: SubscriberGroupsFormState;
  shouldPrompt: boolean;
  mainLanguage: number;
  validation: SubscriberGroupItemValidation;
  translation: SubscriberGroupsTranslation;
  children?: React.ReactNode;
  switchTranslationsToggle: (value: boolean) => void;
  saveSubscriberGroup: (subscriberGroup: SubscriberGroupItem) => void;
  mergeSubscriberGroupItem: () => void;
  updateValidation: (validation: SubscriberGroupItemValidation) => void;
  displayError: (errorMessage: string) => void;
}

export interface SubscriberGroupFormProps {
  actionType?: SubscriberGroupActionType;
  formState: SubscriberGroupsFormState;
  shouldPrompt: boolean;
  mainLanguage: number;
  options: {
    [key: string]: {
      fetching: boolean;
      values?: ComplexValue<number>[];
    };
  };
  section: SubscriberGroupSectionType;
  locale: string;
  translation: SubscriberGroupsTranslation;
  showTranslations: boolean;
  saveSubscriberGroup: (item: SubscriberGroupItem) => void;
  updateItem: (item: Partial<SubscriberGroupItem>) => void;
  changeLanguage: (languageId: number, text: string) => void;
  copyLanguageToAll: (languageId: number, successMessage: string) => void;
  changeLongDescription: (languageId: number, text: string) => void;
  copyLongDescriptionToAll: (languageId: number, successMessage: string) => void;
  switchTranslationsToggle: (showTranslations: boolean) => void;
  updateValidation: (validation: SubscriberGroupItemValidation) => void;
  displayError: (errorMessage: string) => void;
  updateExternalMemberItem: (item: ExternalMemberValue) => void;
  removeExternalMember: (id: number) => void;
  addExternalMember: () => void;
  mergeSubscriberGroupItem: () => void;
  resetValidation: (id: number, key?: keyof ExternalMemberValue) => void;
  removeAllExternalMembers: () => void;
}

export interface SubscriberGroupLanguageProps extends WithMetroLineWrappedComponentProps<SubscriberGroupSectionType> {
  language: ComplexValue<number>[];
  longDescription: ComplexValue<number>[];
  requiredLongDescription?: boolean;
  mainLanguage: number;
  languageOptions: ComplexValue<number>[];
  showTranslations: boolean;
  validation: SubscriberGroupItemValidation;
  translation: SubscriberGroupsTranslation;
  section: string;
  switchTranslationsToggle: (value: boolean) => void;
  changeLanguage: (languageId: number, text: string) => void;
  copyLanguageToAll: (languageId: number, successMessage: string) => void;
  changeLongDescription: (languageId: number, text: string) => void;
  copyLongDescriptionToAll: (languageId: number, successMessage: string) => void;
}

export interface SubscriberGroupStickyHeaderProps {
  actionType: SubscriberGroupActionType;
  shouldPrompt: boolean;
  subscriberGroupItem: SubscriberGroupItem;
  name: string;
  fetching: boolean;
  translation: SubscriberGroupsTranslation;
  mergeSubscriberGroupItem: () => void;
  displayError: (errorMessage: string) => void;
  onSave: () => void;
}

export enum SubscriberGroupActionType {
  add = 'add',
  edit = 'edit'
}

export interface ExternalMemberItemProps extends ExternalMembersTilesProps {
  item: ExternalMemberValue;
}

export interface ExternalMembersTilesProps {
  subscriberGroupItem: SubscriberGroupItem;
  locale: string;
  validation: SubscriberGroupItemValidation;
  translation: SubscriberGroupsTranslation;
  updateExternalMemberItem: (value: ExternalMemberValue) => void;
  removeExternalMember: (id: number) => void;
  removeAllExternalMembers: () => void;
  addExternalMember: () => void;
  resetValidation: (id: number, key?: keyof ExternalMemberValue) => void;
}

export interface ExternalMemberWire {
  emailAddress: string;
  name: string;
}
